import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import axios from 'axios';
import { useStripeApi } from '../../hooks/useStripeApi';
import {
  aggregatePaidInvoicesByCustomer,
  buildCustomerSearchQuery,
  formatCustomerDisplay,
  stripeDashboardBase,
  type StripeCustomer,
  type StripePaidAgg,
} from '../../services/stripeApi';
import {
  listStripeCustomersResolved,
  listStripePaidInvoicesBatchResolved,
  searchStripeCustomersResolved,
} from '../../services/stripeResolved';
import { fmtStripeMoney, fmtStripeUnix, StripeHero, StripeStatCard } from '../../components/stripe/stripeShared';

const StripeCustomersPage: React.FC = () => {
  const { settings, isSecretConfigured, localSecretKey } = useStripeApi();
  const dashBase = localSecretKey ? stripeDashboardBase(localSecretKey) : 'https://dashboard.stripe.com';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<StripeCustomer[]>([]);
  const [customerPaidMap, setCustomerPaidMap] = useState<Map<string, StripePaidAgg>>(new Map());
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    try {
      const paidPromise = listStripePaidInvoicesBatchResolved(settings, isSecretConfigured, 100);
      if (searchDebounced.trim()) {
        const q = buildCustomerSearchQuery(searchDebounced);
        const res = await searchStripeCustomersResolved(settings, isSecretConfigured, q, 40);
        const paidInv = await paidPromise;
        setCustomers(res.data);
        setHasMore(res.has_more);
        setCursor(undefined);
        setCustomerPaidMap(aggregatePaidInvoicesByCustomer(paidInv));
      } else {
        const res = await listStripeCustomersResolved(settings, isSecretConfigured, { limit: 25 });
        const paidInv = await paidPromise;
        setCustomers(res.data);
        setHasMore(res.has_more);
        const last = res.data[res.data.length - 1];
        setCursor(last?.id);
        setCustomerPaidMap(aggregatePaidInvoicesByCustomer(paidInv));
      }
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [settings, isSecretConfigured, searchDebounced]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    if (!cursor || searchDebounced.trim()) {
      return;
    }
    setLoading(true);
    try {
      const res = await listStripeCustomersResolved(settings, isSecretConfigured, {
        limit: 25,
        starting_after: cursor,
      });
      setCustomers((prev) => [...prev, ...res.data]);
      setHasMore(res.has_more);
      const last = res.data[res.data.length - 1];
      setCursor(last?.id);
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    let totalPaid = 0;
    let currency = 'usd';
    let delinquent = 0;
    for (const c of customers) {
      if (c.delinquent) {
        delinquent += 1;
      }
      const agg = customerPaidMap.get(c.id);
      if (agg) {
        totalPaid += agg.totalCents;
        currency = agg.currency;
      }
    }
    return { count: customers.length, totalPaid, currency, delinquent };
  }, [customers, customerPaidMap]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <StripeHero
          compact
          title="Stripe · Customers"
          subtitle="Search by name or email. Total paid sums from the latest 100 paid invoices."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StripeStatCard
            icon={<PeopleOutlineIcon />}
            label="Customers shown"
            value={String(summary.count)}
            sub={searchDebounced ? 'Search results' : 'Current page'}
            color="#635bff"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StripeStatCard
            icon={<PaidOutlinedIcon />}
            label="Total paid (sample)"
            value={fmtStripeMoney(summary.totalPaid, summary.currency)}
            sub="From paid invoices batch"
            color="#0d9488"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StripeStatCard
            icon={<WarningAmberOutlinedIcon />}
            label="Delinquent"
            value={String(summary.delinquent)}
            sub="On current list"
            color="#ef4444"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            size="small"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 320 }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>Customer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Total paid (sample)</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((c) => {
              const agg = customerPaidMap.get(c.id);
              const open = expandedId === c.id;
              const tx = agg?.transactions ?? [];
              return (
                <React.Fragment key={c.id}>
                  <TableRow hover selected={open}>
                    <TableCell>
                      {tx.length > 0 && (
                        <IconButton size="small" onClick={() => setExpandedId(open ? null : c.id)}>
                          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={800}>{formatCustomerDisplay(c)}</Typography>
                    </TableCell>
                    <TableCell>{c.email || '—'}</TableCell>
                    <TableCell>
                      {agg ? fmtStripeMoney(agg.totalCents, agg.currency) : '—'}
                      {tx.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tx.length} payment{tx.length === 1 ? '' : 's'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStripeUnix(c.created)}</TableCell>
                    <TableCell align="right">
                      <Link href={`${dashBase}/customers/${c.id}`} target="_blank" rel="noopener noreferrer">
                        <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                      </Link>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: open ? undefined : 0 }} colSpan={6}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Invoice payments (paid)
                          </Typography>
                          {tx.length ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>When</TableCell>
                                  <TableCell>Invoice</TableCell>
                                  <TableCell align="right">Paid</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {tx.map((t) => (
                                  <TableRow key={t.id}>
                                    <TableCell>{fmtStripeUnix(t.created)}</TableCell>
                                    <TableCell>{t.number || 'Invoice'}</TableCell>
                                    <TableCell align="right">{fmtStripeMoney(t.amountPaid, t.currency)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No paid invoices in sample.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!loading && customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No customers match.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {hasMore && !searchDebounced.trim() && (
        <Button sx={{ mt: 2 }} disabled={loading} onClick={() => void loadMore()}>
          {loading ? <CircularProgress size={22} /> : 'Load more'}
        </Button>
      )}
    </Box>
  );
};

export default StripeCustomersPage;
